import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useUserProfile } from '@/contexts/UserProfileContext';
import { useConcerts } from './useConcerts';
import { useContacts } from './useContacts';
import { useVenues } from './useVenues';

export interface OnboardingStatus {
  hasArtist: boolean;
  hasVenue: boolean;
  hasConcert: boolean;
  isCompleted: boolean;
  isDismissed: boolean;
  tourCompleted: boolean;
}

export function useOnboardingStatus() {
  const { userProfile } = useUserProfile();
  const { data: contacts = [] } = useContacts();
  const { data: venues = [] } = useVenues();
  const { data: concerts = [] } = useConcerts();

  return useQuery({
    queryKey: ['onboarding-status', userProfile?.id],
    queryFn: async (): Promise<OnboardingStatus> => {
      if (!userProfile?.id) {
        throw new Error('User profile not found');
      }

      // Check if user has created required entities
      const hasArtist = contacts.some(c => c.role === 'artist');
      const hasVenue = venues.length > 0;
      const hasConcert = concerts.length > 0;

      // Get onboarding status from user_profiles
      const { data, error } = await supabase
        .from('user_profiles')
        .select('onboarding_completed, onboarding_dismissed, tour_completed')
        .eq('id', userProfile.id)
        .single();

      if (error) throw error;

      return {
        hasArtist,
        hasVenue,
        hasConcert,
        isCompleted: data?.onboarding_completed || false,
        isDismissed: data?.onboarding_dismissed || false,
        tourCompleted: data?.tour_completed || false,
      };
    },
    enabled: !!userProfile?.id,
  });
}

export function useUpdateOnboardingStatus() {
  const queryClient = useQueryClient();
  const { userProfile } = useUserProfile();

  return useMutation({
    mutationFn: async ({ 
      completed, 
      dismissed,
      tourCompleted 
    }: { 
      completed?: boolean; 
      dismissed?: boolean;
      tourCompleted?: boolean;
    }) => {
      if (!userProfile?.id) {
        throw new Error('User profile not found');
      }

      const updateData: any = {};
      
      if (completed !== undefined) {
        updateData.onboarding_completed = completed;
        if (completed) {
          updateData.onboarding_completed_at = new Date().toISOString();
        }
      }
      
      if (dismissed !== undefined) {
        updateData.onboarding_dismissed = dismissed;
      }

      if (tourCompleted !== undefined) {
        updateData.tour_completed = tourCompleted;
        if (tourCompleted) {
          updateData.tour_completed_at = new Date().toISOString();
        }
      }

      const { error } = await supabase
        .from('user_profiles')
        .update(updateData)
        .eq('id', userProfile.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-status'] });
    },
  });
}
